import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AuthProvider, { AuthContext } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import RegisterClient from './pages/RegisterClient';
import UpdateRegistration from './pages/UpdateRegistration';
import VerifyClientEmail from './pages/VerifyClientEmail';
import SetupWizard from './pages/SetupWizard';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import ClientForm from './pages/ClientForm';
import ClientDashboard from './pages/ClientDashboard';
import AdminDashboard from './pages/AdminDashboard';
import CompanyConfig from './pages/CompanyConfig';
import ManageUsers from './pages/ManageUsers';
import ManageRoles from './pages/ManageRoles';
import AssignPermission from './pages/AssignPermission';
import ClientProfile from './pages/ClientProfile';
import ManageInterestRates from './pages/ManageInterestRates';
import ManageLoanReasons from './pages/ManageLoanReasons';
import ManageLoanExpenseTypes from './pages/ManageLoanExpenseTypes';
import ManageClients from './pages/ManageClients';
import ManageLoans from './pages/ManageLoans'; // Added import
import Repayments from './pages/Repayments';
import ManageLoanContracts from './pages/ManageLoanContracts'; // Added import
import ClientContracts from './pages/ClientContracts'; // Added import for client contracts
import SendMessages from './pages/SendMessages';
import Reports from './pages/Reports';
import NamfisaReports from './pages/NamfisaReports';
import ComplianceCases from './pages/ComplianceCases';
import DocumentFlags from './pages/DocumentFlags';
import Audits from './pages/Audits';
import MyPermissions from './pages/MyPermissions';

import SupportTickets from './pages/SupportTickets';
import SupportTicketPublic from './pages/SupportTicketPublic';
import TermsAndConditions from './pages/TermsAndConditions';

import DashboardLayout from './components/DashboardLayout';
import { getCompanyConfigCached } from './utils/invoiceUtils';
import GlobalActivityIndicator from './components/UI/GlobalActivityIndicator';

// Import global styles
import './App.css';
import './styles/typography.css';

const pageTitleByPath = {
  '/': 'Home',
  '/login': 'Login',
  '/setup': 'Setup',
  '/forgot-password': 'Forgot Password',
  '/reset-password': 'Reset Password',
  '/terms': 'Terms and Conditions',
  '/change-password': 'Change Password',
  '/dashboard': 'Dashboard',
  '/admin': 'Admin Dashboard',
  '/company-config': 'Company Configuration',
  '/manage-users': 'Users',
  '/manage-roles': 'Roles',
  '/assign-permission': 'Assign Permissions',
  '/manage-interest-rates': 'Interest Rates',
  '/manage-loan-reasons': 'Loan Reasons',
  '/manage-loan-expenses': 'Loan Expense Types',
  '/manage-clients': 'Clients',
  '/manage-loans': 'Loans',
  '/manage-loan-contracts': 'Loan Contracts',
  '/messages': 'Messages',
  '/audits': 'Audit Trail',
  '/my-permissions': 'My Permissions',
  '/client/profile': 'Client Profile',
  '/client-form': 'Client Form',
  '/client-contracts': 'My Loan Contracts',
  '/reports': 'Reports',
  '/namfisa-reports': 'NAMFISA Reports',
  '/compliance-cases': 'Consumer Protection & Compliance',
  '/document-flags': 'Flagged Documents',
  '/support': 'Support Tickets'
};

const resolvePageTitle = (pathname = '') => {
  if (!pathname) return 'Loan Management System';
  if (pathname.startsWith('/support/support/')) return 'Support Ticket';
  if (pathname.startsWith('/roles/')) return 'Role Permissions';
  return pageTitleByPath[pathname] || 'Loan Management System';
};

const DocumentTitleSync = () => {
  const location = useLocation();
  const [companyName, setCompanyName] = React.useState('Loan Management System');

  React.useEffect(() => {
    let active = true;
    (async () => {
      try {
        const cfg = await getCompanyConfigCached();
        const name = String(cfg?.company_name || '').trim();
        if (active && name) setCompanyName(name);
      } catch {
        // Keep fallback title
      }
    })();
    return () => { active = false; };
  }, [location.pathname]);

  React.useEffect(() => {
    const pageTitle = resolvePageTitle(location.pathname);
    document.title = `${companyName} - ${pageTitle}`;
  }, [companyName, location.pathname]);

  return null;
};

// Old /loan-application links open the application panel on the dashboard.
const LoanApplicationRedirect = () => {
  const { search } = useLocation();
  const resume = new URLSearchParams(search).has('resume');
  return <Navigate to={`/dashboard?apply=${resume ? 'resume' : '1'}`} replace />;
};

// Permission-aware dashboard component: users with client permissions -> ClientDashboard, otherwise AdminDashboard
const RoleDashboard = () => {
  const { user } = React.useContext(AuthContext);
  if (!user) return null;
  const perms = Array.isArray(user.permissions) ? user.permissions : [];
  const isClient = perms.includes('SignDocuments') || perms.includes('ViewProfile');
  return isClient ? <ClientDashboard /> : <AdminDashboard />;
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user } = React.useContext(AuthContext);
  const location = useLocation();

  // Not authenticated or not allowed
  if (!user || (allowedRoles && !allowedRoles.includes(user.roleId))) {
    return <Navigate to="/login" replace />;
  }

  // Force password change: only allow accessing /change-password until completed
  if (user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // Global admin setup enforcement so even non-permission routes (e.g., /admin) get redirected
  const [needsSetup, setNeedsSetup] = React.useState(false);
  React.useEffect(() => {
    (async () => {
      try {
        const cfg = await getCompanyConfigCached();
        const ok = cfg && [
          cfg.company_name,
          cfg.address,
          cfg.email,
          cfg.phone_number,
          cfg.bank_name,
          cfg.branch_code,
          cfg.account_number,
          cfg.account_type
        ].every(v => v !== null && v !== undefined && String(v).trim());
        const adminLike = Array.isArray(user.permissions) && user.permissions.includes('ManageConfig');
        setNeedsSetup(adminLike && !ok);
      } catch {
        const adminLike = Array.isArray(user.permissions) && user.permissions.includes('ManageConfig');
        setNeedsSetup(adminLike);
      }
    })();
  }, [user]);
  if (needsSetup && location.pathname !== '/company-config') {
    return <Navigate to="/company-config" replace />;
  }

  return children;
};

// Blocks client-role users (ViewProfile/SignDocuments) from accessing staff-only pages.
const NonClientRoute = ({ children }) => {
  const { user } = React.useContext(AuthContext);
  if (!user) return <Navigate to="/login" replace />;
  const perms = Array.isArray(user.permissions) ? user.permissions : [];
  const isClient = perms.includes('SignDocuments') || perms.includes('ViewProfile');
  if (isClient) return <Navigate to="/dashboard" replace />;
  return children;
};

// Permission-aware route: requires the user to have at least one of the required permissions
const PermissionRoute = ({ children, required }) => {
  const { user } = React.useContext(AuthContext);
  const location = useLocation();
  const requiredList = Array.isArray(required) ? required : [required];

  if (!user) return <Navigate to="/login" replace />;
  if (user.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  // If user is admin-like (can ManageConfig) and company setup incomplete, force them to /company-config
  const [blocked, setBlocked] = React.useState(false);
  React.useEffect(() => {
    (async () => {
      try {
        const cfg = await getCompanyConfigCached();
        const ok = cfg && [
          cfg.company_name,
          cfg.address,
          cfg.email,
          cfg.phone_number,
          cfg.bank_name,
          cfg.branch_code,
          cfg.account_number,
          cfg.account_type
        ].every(v => v !== null && v !== undefined && String(v).trim());
        const adminLike = Array.isArray(user.permissions) && user.permissions.includes('ManageConfig');
        setBlocked(adminLike && !ok);
      } catch {
        const adminLike = Array.isArray(user.permissions) && user.permissions.includes('ManageConfig');
        setBlocked(adminLike);
      }
    })();
  }, [user]);
  if (blocked && location.pathname !== '/company-config') {
    return <Navigate to="/company-config" replace />;
  }
  const has = Array.isArray(user.permissions) && user.permissions.some(p => requiredList.includes(p));
  if (!has) return <Navigate to="/dashboard" replace />;
  return children;
};

const RootRedirect = () => {
  const { user } = React.useContext(AuthContext);
  if (!user) return <Navigate to="/login" />;
  if (user.mustChangePassword) return <Navigate to="/change-password" />;
  return <Navigate to="/dashboard" />;
};

const App = () => {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <GlobalActivityIndicator />
        <DocumentTitleSync />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/register-client" element={<RegisterClient />} />
          <Route path="/update-registration" element={<UpdateRegistration />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-client-email" element={<VerifyClientEmail />} />
          <Route path="/setup" element={<SetupWizard />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<TermsAndConditions />} />

          {/* Tenant-scoped public entry points. Email links land here so the
              login / verify pages can brand themselves with the tenant's
              company name without requiring auth first. */}
          <Route path="/:tenantSlug/login" element={<Login />} />
          <Route path="/:tenantSlug/register-client" element={<RegisterClient />} />
          <Route path="/:tenantSlug/update-registration" element={<UpdateRegistration />} />
          <Route path="/:tenantSlug/verify-email" element={<VerifyEmail />} />
          <Route path="/:tenantSlug/verify-client-email" element={<VerifyClientEmail />} />
          <Route path="/:tenantSlug/forgot-password" element={<ForgotPassword />} />
          <Route path="/:tenantSlug/reset-password" element={<ResetPassword />} />
          <Route path="/:tenantSlug/terms" element={<TermsAndConditions />} />

          {/* Support link (no auth required) */}
          <Route path="/support/support/:supportToken" element={<SupportTicketPublic />} />
          {/* Change Password: protected but rendered outside DashboardLayout */}
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePassword />
              </ProtectedRoute>
            }
          />

          <Route
            element={
              <ProtectedRoute>
                <DashboardLayout />
              </ProtectedRoute>
            }
          >
            {/* Client/staff pages gated by permissions */}
            <Route path="/client-form" element={
              <PermissionRoute required={["ClientOnboarding","ManageClients"]}>
                <ClientForm />
              </PermissionRoute>
            } />
            <Route path="/dashboard" element={<RoleDashboard />} />
            {/* Every signed-in user can see what their role allows */}
            <Route path="/my-permissions" element={<MyPermissions />} />

            <Route path="/support" element={
              <NonClientRoute>
                <SupportTickets />
              </NonClientRoute>
            } />
            <Route path="/client/profile" element={
              <PermissionRoute required={["ViewProfile","SignDocuments"]}>
                <ClientProfile />
              </PermissionRoute>
            } />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/company-config" element={
              <PermissionRoute required={["CompanyConfig","ManageConfig"]}>
                <CompanyConfig />
              </PermissionRoute>
            } />
            <Route path="/manage-users" element={
              <PermissionRoute required="ManageUsers">
                <ManageUsers />
              </PermissionRoute>
            } />
            <Route
              path="/manage-roles"
              element={
                <PermissionRoute required="ManageConfig">
                  <ManageRoles />
                </PermissionRoute>
              }
            />
            <Route
              path="/assign-permission"
              element={
                <PermissionRoute required="ManageConfig">
                  <AssignPermission />
                </PermissionRoute>
              }
            />
            <Route
              path="/roles/:roleId/permissions"
              element={
                <PermissionRoute required="ManageConfig">
                  <AssignPermission />
                </PermissionRoute>
              }
            />
            <Route path="/manage-interest-rates" element={
              <PermissionRoute required="ManageConfig">
                <ManageInterestRates />
              </PermissionRoute>
            } />
            <Route path="/manage-loan-reasons" element={
              <PermissionRoute required={["LoanReason.View","LoanReason.Create","LoanReason.Edit","LoanReason.Toggle","ManageConfig"]}>
                <ManageLoanReasons />
              </PermissionRoute>
            } />
            <Route path="/manage-loan-expenses" element={
              <PermissionRoute required="ManageConfig">
                <ManageLoanExpenseTypes />
              </PermissionRoute>
            } />
            <Route path="/manage-clients" element={
              <PermissionRoute required="ManageClients">
                <ManageClients />
              </PermissionRoute>
            } />
            <Route path="/manage-loans" element={
              <PermissionRoute required={["ViewLoans","ManageLoans"]}>
                <ManageLoans />
              </PermissionRoute>
            } /> {/* Added route with permission guard */}
            <Route path="/repayments" element={
              <PermissionRoute required={['*', 'ViewLoans', 'ManageLoans', 'EnterRepayment']}>
                <Repayments />
              </PermissionRoute>
            } />
            <Route path="/manage-loan-contracts" element={
              <PermissionRoute required="ManageLoans">
                <ManageLoanContracts />
              </PermissionRoute>
            } /> {/* Added contract route with guard */}
            <Route path="/send-messages" element={<Navigate to="/messages" replace />} />
            <Route path="/messages" element={
              <PermissionRoute required={["ManageClients","ManageLoans","ManageConfig","ManageUsers"]}>
                <SendMessages />
              </PermissionRoute>
            } />
            <Route path="/audits" element={
              <PermissionRoute required={["AuditTrail","ManageConfig","SignDocuments"]}>
                <Audits />
              </PermissionRoute>
            } />
          {/* The loan application now opens from the dashboard ("Apply for a loan"). */}
          <Route path="/loan-application" element={<LoanApplicationRedirect />} />
          <Route path="/my-loans" element={<Navigate to="/dashboard" replace />} />
          <Route path="/client-contracts" element={
            <PermissionRoute required="SignDocuments">
              <ClientContracts />
            </PermissionRoute>
          } /> {/* Added client contracts route with guard */}
          <Route path="/reports" element={
            <PermissionRoute required="GenerateReports">
              <Reports />
            </PermissionRoute>
          } />
          <Route path="/namfisa-reports" element={
            <PermissionRoute required={["GenerateReports","ManageConfig"]}>
              <NamfisaReports />
            </PermissionRoute>
          } />
          <Route path="/compliance-cases" element={
            <PermissionRoute required="ManageCompliance">
              <ComplianceCases />
            </PermissionRoute>
          } />
          <Route path="/document-flags" element={
            <PermissionRoute required={['ManageClients', 'ManageLoans', 'ViewLoans']}>
              <DocumentFlags />
            </PermissionRoute>
          } />
          </Route>
          <Route path="/" element={<RootRedirect />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
};

export default App;